import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import PoolOutlinedIcon from '@mui/icons-material/PoolOutlined';
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import StarsOutlinedIcon from '@mui/icons-material/StarsOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { AccountDetails, clearSession, getAccountDetails, getPartnerDashboard, isAdmin, isLoggedIn, venueImageUrl } from '../../api';
import { PortalContext } from './shared';
import { useFeatures } from '../../features';
import './partner.css';

// Sidebar from the proposal image. Sections without a backend yet are marked "Soon" and open an
// honest placeholder rather than fake data. adminOnly sections show only in the admin console's
// act-as view (?restoId=), never to the venue's own partner login.
const MENU = [
  { path: '', label: 'Dashboard', icon: GridViewRoundedIcon, end: true },
  { path: '/offers', label: 'My Offers', icon: LocalOfferIcon },
  { path: '/offer-assistant', label: 'Offer Assistant', icon: AutoAwesomeOutlinedIcon },
  { path: '/venue', label: 'Venue Profile', icon: StorefrontOutlinedIcon },
  { path: '/gallery', label: 'Image Gallery', icon: PhotoLibraryOutlinedIcon },
  // Only while offer claiming is switched on (it's off: Cheers2Night is discovery-only).
  { path: '/claims', label: 'Bookings & Claims', icon: EventAvailableOutlinedIcon, claimsOnly: true },
  { path: '/events', label: 'Events', icon: CelebrationOutlinedIcon },
  { path: '/pool-parties', label: 'Pool Parties', icon: PoolOutlinedIcon },
  { path: '/analytics', label: 'Analytics', icon: InsertChartOutlinedIcon, adminOnly: true },
  { path: '/reviews', label: 'Reviews', icon: StarsOutlinedIcon, soon: true },
  { path: '/payouts', label: 'Payouts', icon: AccountBalanceWalletOutlinedIcon, soon: true },
  { path: '/settings', label: 'Account Settings', icon: ManageAccountsOutlinedIcon },
  { path: '/support', label: 'Support', icon: SupportAgentOutlinedIcon }
];

// Shell for every /partner-account/* page: where a partner lands after login. Admins reach it from
// the admin console with ?restoId=<id> to manage that venue as if they were its owner.
const PartnerLayout: React.FC = () => {
  const features = useFeatures();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const adminRestoId = isAdmin() ? Number(searchParams.get('restoId')) || null : null;
  const [account, setAccount] = useState<AccountDetails | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [newClaims, setNewClaims] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  // Phones/tablets: the sidebar is a slide-in drawer opened from the top bar.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const allowed = isLoggedIn() && !(isAdmin() && adminRestoId === null);

  const refreshDashboard = useCallback(() => {
    getPartnerDashboard().then((d) => setNewClaims(d.claimsLast24h)).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!allowed) return;
    setLoadError(false);
    getAccountDetails().then(setAccount).catch(() => setLoadError(true));
    refreshDashboard();
  }, [allowed, adminRestoId, refreshDashboard]);

  // Close the account menu and the drawer on navigation or an outside click.
  useEffect(() => {
    setMenuOpen(false);
    setDrawerOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    // Stop the page behind the drawer from scrolling.
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [drawerOpen]);
  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const to = useCallback(
    (path: string) => (adminRestoId !== null ? `${path}${path.includes('?') ? '&' : '?'}restoId=${adminRestoId}` : path),
    [adminRestoId]
  );

  if (!isLoggedIn()) return <Navigate to="/login?role=partner" replace />;
  if (!allowed) return <Navigate to="/admin" replace />;

  const handleLogout = () => {
    clearSession();
    navigate('/');
  };

  if (loadError) {
    return (
      <div className="pp" style={{ display: 'block' }}>
        <div className="pp-main" style={{ maxWidth: 520, margin: '80px auto' }}>
          <div className="pp-notice error">Could not load your account. Your session may have expired.</div>
          <button className="pp-btn pp-gold" onClick={() => { clearSession(); navigate('/login?role=partner'); }}>Log in again</button>
        </div>
      </div>
    );
  }

  const avatar = venueImageUrl(account?.filePath);
  const context: PortalContext = { account, to, refreshDashboard };

  return (
    <div className="pp">
      {drawerOpen && <div className="pp-backdrop" onClick={() => setDrawerOpen(false)} aria-hidden="true" />}
      <aside className={`pp-side ${drawerOpen ? 'open' : ''}`} id="pp-drawer">
        <div className="pp-side-head">
          <Link className="pp-logo" to="/" title="Go to the Cheers2Night website"><img src="/logo2.png" alt="Cheers2Night" /></Link>
          <button type="button" className="pp-icon-btn pp-mobile-only" onClick={() => setDrawerOpen(false)} aria-label="Close menu"><CloseIcon /></button>
        </div>
        <nav className="pp-menu" aria-label="Partner portal">
          {MENU.filter((item) => (!item.adminOnly || adminRestoId !== null) && (!item.claimsOnly || features.offerClaims)).map(({ path, label, icon: Icon, end, soon }) => (
            <NavLink key={label} to={to(`/partner-account${path}`)} end={end}>
              <Icon />{label}{soon && <span className="pp-soon">SOON</span>}
            </NavLink>
          ))}
          <NavLink to={to('/pos')}><PointOfSaleOutlinedIcon />POS &amp; Billing</NavLink>
        </nav>
        <div className="pp-side-spacer" />
        <Link className="pp-promo" to={to('/partner-account/offers/new')} style={{ backgroundImage: 'url(/hero.jpg)' }}>
          <span>Grow Your Business With <b>Cheers2Night</b></span>
        </Link>
        <Link className="pp-help" to={to('/partner-account/support')}>
          <HeadsetMicOutlinedIcon />
          <span>Need Help?<small>Contact Support</small></span>
        </Link>
      </aside>

      <div className="pp-shell">
        <header className="pp-topbar">
          <div className="pp-topbar-left">
            <button type="button" className="pp-icon-btn pp-burger" onClick={() => setDrawerOpen(true)} aria-label="Open menu" aria-expanded={drawerOpen} aria-controls="pp-drawer"><MenuIcon /></button>
            <Link className="pp-topbar-logo" to={to('/partner-account')}><img src="/logo2.png" alt="Cheers2Night" /></Link>
            <h1>Partner Portal</h1>
          </div>
          <div className="pp-topbar-right">
            {features.offerClaims && (
              <Link className="pp-bell" to={to('/partner-account/claims')} aria-label={`${newClaims} new claims in the last 24 hours`} title="New claims (last 24 hours)">
                <NotificationsNoneOutlinedIcon />
                {newClaims > 0 && <i>{newClaims > 99 ? '99+' : newClaims}</i>}
              </Link>
            )}
            <div className="pp-user" ref={menuRef}>
              <button onClick={() => setMenuOpen((o) => !o)} aria-expanded={menuOpen} aria-haspopup="menu">
                {avatar ? <img className="pp-avatar" src={avatar} alt="" /> : <span className="pp-avatar">{(account?.name ?? 'C')[0]}</span>}
                <span><b>{account?.name ?? 'Loading...'}</b><small>{adminRestoId !== null ? 'Admin view' : 'Partner'}</small></span>
                <KeyboardArrowDownIcon />
              </button>
              {menuOpen && (
                <div className="pp-dropdown" role="menu">
                  <Link role="menuitem" to={to('/partner-account/venue')}><StorefrontOutlinedIcon />Venue Profile</Link>
                  <Link role="menuitem" to={to('/partner-account/settings')}><ManageAccountsOutlinedIcon />Account Settings</Link>
                  <Link role="menuitem" to="/"><OpenInNewIcon />View Website</Link>
                  {adminRestoId !== null
                    ? <button role="menuitem" onClick={() => navigate('/admin')}><LogoutIcon />Back to Admin</button>
                    : <button role="menuitem" className="danger" onClick={handleLogout}><LogoutIcon />Logout</button>}
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="pp-main">
          {adminRestoId !== null && (
            <div className="pp-notice info">Admin mode: changes you make here apply to this venue's live listing.</div>
          )}
          <Outlet context={context} />
        </main>
      </div>
    </div>
  );
};

export default PartnerLayout;
